import React, { useState } from 'react'
import { 
  Eye, 
  Lock, 
  Unlock, 
  Play, 
  Download,
  Share2,
  Heart,
  Star,
  DollarSign,
  Clock,
  Users,
  CheckCircle
} from 'lucide-react'

interface ContentItem {
  id: string
  title: string
  description: string
  thumbnail: string
  price: number
  currency: string
  duration?: string
  category: string
  creator: string
  isPurchased: boolean
  previewUrl?: string
  fullContentUrl?: string
  stats: {
    views: number
    likes: number
    purchases: number
    rating?: number
    reviews?: number
  }
}

interface PayPerViewProps {
  content: ContentItem[]
  onPurchase: (contentId: string, price: number) => void
  onView: (contentId: string) => void
  isLoading?: boolean
  userWallet?: string
}

export default function PayPerView({ 
  content, 
  onPurchase, 
  onView,
  isLoading = false,
  userWallet
}: PayPerViewProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'newest' | 'price' | 'popular'>('newest')

  const categories = ['all', 'video', 'audio', 'document', 'image', 'course']

  const filteredContent = content
    .filter(item => selectedCategory === 'all' || item.category === selectedCategory)
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price
        case 'popular':
          return b.stats.purchases - a.stats.purchases
        case 'newest':
        default:
          return new Date().getTime() - new Date().getTime() // Mock newest
      }
    })

  const formatCurrency = (amount: number, currency: string) => `${amount} ${currency}`
  const formatDuration = (duration?: string) => duration ? `${duration}` : 'N/A'

  const handlePurchase = (item: ContentItem) => {
    if (item.isPurchased) {
      onView(item.id)
    } else {
      onPurchase(item.id, item.price)
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'video': return <Play className="w-4 h-4" />
      case 'audio': return <Eye className="w-4 h-4" />
      case 'document': return <Download className="w-4 h-4" />
      case 'image': return <Eye className="w-4 h-4" />
      case 'course': return <Star className="w-4 h-4" />
      default: return <Eye className="w-4 h-4" />
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Premium Content
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Access exclusive content from your favorite creators. Pay once, own forever on the blockchain.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap justify-between items-center mb-8 bg-white rounded-lg shadow-sm p-4">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-4 md:mb-0">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category === 'all' ? 'All Content' : category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Sort Filter */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="newest">Newest First</option>
          <option value="price">Price: Low to High</option>
          <option value="popular">Most Popular</option>
        </select>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredContent.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
          >
            {/* Thumbnail */}
            <div className="relative aspect-video bg-gradient-to-br from-purple-100 to-pink-100">
              <img
                src={item.thumbnail}
                alt={item.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {item.isPurchased ? (
                    <button
                      onClick={() => handlePurchase(item)}
                      className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-200"
                    >
                      <Play className="w-6 h-6" />
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePurchase(item)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center"
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        {formatCurrency(item.price, item.currency)}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Badge */}
              <div className="absolute top-3 left-3">
                {item.isPurchased ? (
                  <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Owned
                  </span>
                ) : (
                  <span className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <Lock className="w-3 h-3 mr-1" />
                    Premium
                  </span>
                )}
              </div>

              {/* Category Badge */}
              <div className="absolute top-3 right-3">
                <span className="bg-gray-800 bg-opacity-80 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  {getCategoryIcon(item.category)}
                  <span className="ml-1 capitalize">{item.category}</span>
                </span>
              </div>
            </div>

            {/* Content Info */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                  {item.title}
                </h3>
                <div className="text-right ml-2">
                  <div className="text-xl font-bold text-purple-600">
                    {formatCurrency(item.price, item.currency)}
                  </div>
                  {item.duration && (
                    <div className="text-sm text-gray-500 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDuration(item.duration)}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {item.description}
              </p>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <Eye className="w-4 h-4 mr-1" />
                    {item.stats.views}
                  </span>
                  <span className="flex items-center">
                    <Heart className="w-4 h-4 mr-1" />
                    {item.stats.likes}
                  </span>
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {item.stats.purchases}
                  </span>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => handlePurchase(item)}
                disabled={isLoading}
                className={`w-full py-2 px-4 rounded-lg font-semibold transition-all duration-200 ${
                  item.isPurchased
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                } ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : item.isPurchased ? (
                  <span className="flex items-center justify-center">
                    <Unlock className="w-4 h-4 mr-2" />
                    View Content
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Lock className="w-4 h-4 mr-2" />
                    Purchase & View
                  </span>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredContent.length === 0 && (
        <div className="text-center py-12">
          <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No premium content found
          </h3>
          <p className="text-gray-600">
            Try adjusting your filters or check back later for new content.
          </p>
        </div>
      )}

      {/* Benefits Section */}
      <div className="mt-16 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">
          Why Pay-Per-View on Harmony Hub?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 text-purple-600 mb-4">
              <CheckCircle className="w-6 h-6" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">True Ownership</h4>
            <p className="text-gray-600 text-sm">
              Once purchased, you own the content forever on the blockchain.
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4">
              <Share2 className="w-6 h-6" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Creator Benefits</h4>
            <p className="text-gray-600 text-sm">
              Creators keep 90% of earnings with transparent blockchain payments.
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-4">
              <DollarSign className="w-6 h-6" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Fair Pricing</h4>
            <p className="text-gray-600 text-sm">
              No hidden fees, no platform cuts - just fair compensation for creators.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
